import React, { useState } from 'react';
import { Check, X, Loader2, Undo2, ChevronDown, ChevronRight } from 'lucide-react';
import { settingsWriteApi } from '../lib/api';
import { Button } from './ui/button';

/**
 * The assistant proposing that one slot's settings be copied onto another, and the customer
 * accepting it.
 *
 * <p>This is ProposalCard's problem at a different scale, and the scale is what drives the design.
 * A proposal is a handful of named settings, so it can be listed in full and read in full. A copy
 * is six hundred of them. Printing six hundred rows into a 380px chat bubble is not review - it is
 * a wall that gets scrolled past, and "I scrolled past it" is exactly the consent we must not
 * collect before overwriting a working slot. So the summary is the primary object here and the
 * rows are a disclosure underneath it: how many change, how many already match, and - the part
 * that actually protects people - how the changes distribute across SECTIONS. Someone who asked
 * for "the same pacing as slot1" needs to see the line that says the copy also lands 63 changes in
 * post scheduling, and they need to see it without opening anything.
 *
 * <p>Every number and every phrase here comes from the server's dry run of the real write path
 * against the customer's own slots, and is rendered rather than recomputed. That matters most for
 * two of them. The counts are totals, so they stay exact even when the row list is capped
 * (diffTruncated) - a truncated list must never quietly become a smaller promise. And the
 * exclusion line is echoed VERBATIM: which fields are too sensitive to travel is a server-side
 * policy that has already changed once, and a copy of that list living in the UI is a copy that
 * will eventually disagree with the code doing the writing, in the reassuring direction.
 *
 * <p>The diff rows here carry old/new. ProposalCard's carry oldValue/newValue. They come from two
 * different endpoints and are two different contracts that happen to look alike; unifying them in
 * the UI would just mean one of the two cards silently renders "undefined -> undefined" the next
 * time either server changes. Left deliberately apart.
 *
 * <p>Applying goes through the same public bulk-copy endpoint the slots page uses, so the change
 * lands in the target's ordinary settings history with the ordinary undo - which is the only
 * reason it is reasonable to offer a 214-value overwrite behind one button.
 */
export default function CopyProposalCard({ copy, onDone, onDismiss }) {
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [showRows, setShowRows] = useState(false);

  if (!copy) return null;

  // The server refused to plan it at all - offer no button, just the reason. Same box as
  // ProposalCard's refusal, because to the customer this is the same event.
  if (copy.problem) {
    return (
      <div className="rounded-lg border border-warning/30 bg-warning/5 p-3 text-xs text-muted-foreground">
        <span className="font-semibold text-warning">Could not prepare that change.</span>{' '}
        {copy.problem}
      </div>
    );
  }

  // One target at a time is the contract; anything else the server sends back as a `problem`.
  const row = (copy.targets || [])[0];
  if (!row) return null;

  const target = copy.target || row.slot;
  const changed = row.changed || 0;
  const unchanged = row.unchanged || 0;
  const diff = row.diff || [];
  const bySection = row.bySection || [];
  const excluded = copy.excluded || [];
  const unreadable = (copy.sourceUnreadable || []).length;
  // ok is only false when the server actively could not write the slot; a missing key is a plain
  // preview row, not a failure.
  const writable = row.ok !== false;

  if (result) {
    return (
      <div className="rounded-lg border border-success/30 bg-success/5 p-3 text-xs" data-testid="assistant-copy-done">
        <p className="text-success font-semibold">
          {`Copied ${result.count} value(s) from ${copy.source} to ${target}.`}
          {/* The applied count is the server's, not the preview's. They differ when the source
            * moved between the dry run and the press, and saying so is cheaper than letting
            * someone reconcile two numbers on their own. */}
          {result.count !== result.previewed
            && ` (the preview said ${result.previewed}; ${copy.source} changed in between)`}
        </p>
        <p className="mt-1 flex items-center gap-1 text-muted-foreground">
          <Undo2 className="h-3 w-3" aria-hidden />
          {`Undo it any time from ${target}'s settings history.`}
        </p>
        <p className="mt-1 text-muted-foreground">
          {`Ask me again now and I'll suggest the warm-up numbers against the copied values.`}
        </p>
        {result.blocked && result.blocked.length > 0 && (
          <p className="mt-2 text-warning" data-testid="assistant-copy-blocked">
            {`${result.blocked.length} setting${result.blocked.length === 1 ? '' : 's'} did not land on ${target}: `}
            {result.blocked.map((b) => `${b.label || b.field || b.id}${b.reason ? ` (${b.reason})` : ''}`).join('; ')}
          </p>
        )}
      </div>
    );
  }

  const apply = async () => {
    setBusy(true);
    setError(null);
    try {
      /*
       * copy.ids is REPLAYED, not omitted. The sensitive-field exclusion - IG credentials, the
       * phone, the proxy, the email/scraper/OpenAI keys - is applied in the assistant layer when
       * it builds this plan, not by the bulk-copy endpoint. Sending source+target with no ids
       * would make the server re-derive the set as "everything" and carry precisely the fields
       * the preview above promised to leave alone. The id list IS the promise.
       */
      const res = await settingsWriteApi.bulkCopy({
        source: copy.source,
        targets: [target],
        ids: copy.ids,
        dryRun: false,
        // A "full clone" means the login travels too. Without this the server silently
        // withheld the identity fields while this card announced the login had moved.
        allowIdentity: !!copy.includedIdentity,
      });
      const applied = ((res.data && res.data.targets) || [])[0] || {};
      if (applied.ok === false) {
        // Deliberately NOT "nothing was changed": a write can fail after the values were saved.
        setError(`${target} cannot be written: ${applied.error || 'the server refused the write'}`);
        return;
      }
      setResult({
        count: typeof applied.changed === 'number' ? applied.changed : changed,
        previewed: changed,
        // Whatever the server refused is shown, never assumed away: a copy that quietly
        // skipped fields would tell the customer everything moved when some of it did not.
        blocked: Array.isArray(applied.blocked) ? applied.blocked : [],
      });
      if (onDone) onDone();
    } catch (e) {
      setError(e.response?.data?.error || e.response?.data?.message || e.message
        || 'Could not apply the copy.');
    } finally {
      setBusy(false);
    }
  };

  // The server could not write this slot at all - most often because it is not on this account.
  // Everything below assumes a real plan: the counts default to 0, so rendering them here says
  // "0 settings would change - 0 already match", which reads as "the two slots already agree".
  // And the exclusion line would promise to leave fields alone on a slot that does not exist.
  // Show the reason, and nothing that implies a plan.
  if (!writable) {
    return (
      <div className="rounded-lg border border-warning/30 bg-warning/5 p-3 text-xs text-muted-foreground"
        data-testid="assistant-copy-unwritable">
        <span className="font-semibold text-warning">Could not copy to {target}.</span>{' '}
        {row.error || 'the server did not say why'}
      </div>
    );
  }

  return (
    <div className="rounded-lg border border-primary/30 bg-primary/5 p-3 text-xs" data-testid="assistant-copy-proposal">
      <p className="font-semibold text-foreground">
        {`Copy ${copy.source}'s settings to ${target}?`}
      </p>

      <p className="mt-1 text-muted-foreground">
        <span className="font-medium text-foreground">{changed}</span>
        {` setting${changed === 1 ? '' : 's'} would change - `}
        <span className="font-medium text-foreground">{unchanged}</span>
        {' already match'}
      </p>

      {/* The blast radius, and the reason this card exists at all: it is what tells someone who
        * asked about pacing that the copy also reaches posting. */}
      {bySection.length > 0 && (
        <ul className="mt-2 space-y-0.5 rounded-md border border-border/60 bg-background/40 p-2">
          {bySection.map((s) => (
            <li key={s.section} className="flex items-baseline justify-between gap-2">
              <span className="min-w-0 text-muted-foreground">{s.section}</span>
              <span className="shrink-0 font-medium text-foreground">{s.changed}</span>
            </li>
          ))}
        </ul>
      )}

      {/* Verbatim from the server. Never assembled here - see the header comment. */}
      {/* Worded as a DEFAULT, deliberately. The first version said "what won\'t travel, no
        * matter what", which read as the assistant overruling the owner on their own
        * account. The exclusions are a sensible default; the customer can ask for any of
        * it and the assistant will carry it out. */}
      {excluded.length > 0 && (
        <p className="mt-2 text-muted-foreground">
          {`Not copied by default, so ${target} keeps its own: `}
          {excluded.join('; ')}
          {'. Ask and I\'ll include them too.'}
        </p>
      )}

      {(copy.includedSensitive || copy.includedIdentity) && (
        <p className="mt-2 text-warning">
          {copy.includedIdentity
            ? `Full clone: ${target} will also take ${copy.source} own Instagram login. Two slots signed into one account will fight each other.`
            : `Also copying the phone, proxy and credentials onto ${target}.`}
        </p>
      )}

      {unreadable > 0 && (
        <p className="mt-1 text-muted-foreground">
          {`${unreadable} value(s) on ${copy.source} could not be read and are left out.`}
        </p>
      )}

      {/* Collapsed by default: the summary above is the review, this is the audit. */}
      {diff.length > 0 && (
        <div className="mt-2">
          <button
            type="button"
            onClick={() => setShowRows((v) => !v)}
            className="tap flex items-center gap-1 rounded-md text-muted-foreground hover:text-foreground"
            aria-expanded={showRows}
            data-testid="assistant-copy-rows-toggle"
          >
            {showRows ? <ChevronDown className="h-3 w-3" aria-hidden />
              : <ChevronRight className="h-3 w-3" aria-hidden />}
            {showRows ? 'Hide the list' : `Show all ${changed}`}
          </button>
          {showRows && (
            <>
              <ul className="mt-1.5 space-y-1">
                {diff.map((c) => (
                  <li key={c.id} className="flex flex-wrap items-baseline gap-x-1.5">
                    <span className="font-medium text-foreground">#{c.id}</span>
                    <span className="text-muted-foreground">{c.label || c.field}</span>
                    <span className="text-muted-foreground/70 line-through break-all">{String(c.old)}</span>
                    <span aria-hidden>-&gt;</span>
                    <span className="font-semibold text-primary break-all">{String(c.new)}</span>
                  </li>
                ))}
              </ul>
              {row.diffTruncated && (
                <p className="mt-1 text-muted-foreground">
                  {`...and ${changed - diff.length} more not listed`}
                </p>
              )}
            </>
          )}
        </div>
      )}

      <p className="mt-2 text-muted-foreground">
        {`Nothing changes until you press Apply. It lands in ${target}'s settings history and can be undone there.`}
      </p>

      {error && <p className="mt-2 text-destructive">{error}</p>}

      <div className="mt-2 flex gap-1.5">
        <Button size="sm" className="h-7 flex-1 text-[11px]" disabled={busy} onClick={apply}>
          {busy ? <Loader2 className="mr-1 h-3 w-3 animate-spin" /> : <Check className="mr-1 h-3 w-3" />}
          Apply
        </Button>
        <Button size="sm" variant="ghost" className="h-7 text-[11px]" disabled={busy} onClick={onDismiss}>
          <X className="mr-1 h-3 w-3" /> No thanks
        </Button>
      </div>
    </div>
  );
}
